import { useEffect } from "react";
import { useNavigate } from "react-router";
import { Spin } from "antd";
import { useAuth } from "@/hooks/useAuth";

/**
 * Home Page
 * Auto-redirect page based on server authentication status
 *
 * Flow:
 * 1. Wait for authentication to complete (LINE login happens automatically in LineProvider)
 * 2. If logged in -> redirect to /reload/qrcode
 * 3. If not logged in -> stay on this page (showing loading state)
 *
 * **Auth Architecture:**
 * - Uses ONLY `useAuth()` to check server login status (single source of truth)
 * - LINE login is automatic - triggered by LineProvider when LIFF initializes
 * - AuthProvider automatically exchanges LINE tokens for server tokens
 * - Components never manually trigger login - it's a transparent prerequisite
 */
export default function HomePage() {
  const { isLoggedIn, isAuthenticating, error } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for authentication check to complete
    if (isAuthenticating) return;

    if (isLoggedIn) {
      // Server auth successful -> navigate to main page
      navigate("/reload/qrcode");
    }
    // If not logged in, LineProvider will automatically trigger LINE login
  }, [isAuthenticating, isLoggedIn, navigate]);

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-600">Error</h1>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <p className="text-sm text-gray-500">
            Please try logging in again.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-8">
      <div className="text-center">
        <Spin size="large" />
        <p className="mt-4 text-gray-600">Initializing authentication...</p>
      </div>
    </main>
  );
}
